/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import dayjs from 'dayjs';
import forge from 'node-forge';
import type { ComponentProps } from 'react';
import {
  baseVariables,
  getDateObject,
  getLocalizedDate,
  getDateObjectCurrent,
  eventUtil,
  Calendar,
} from '@marriott/mi-ui-library';
import { ConsentType } from '../store/profileStore';
import {
  BREAKPOINT_DESKTOP,
  BREAKPOINT_TABLET,
  BREAKPOINT_MOBILE_TEXT,
  BREAKPOINT_DESKTOP_TEXT,
  BREAKPOINT_TABLET_TEXT,
  DATA_LAYER_PROPERTIES,
  calendarConstants,
  SNA_Status_REQUESTED,
  SNA_Status_DENIED,
  SNA_Status_GUARANTEED,
  constants,
  DATE_FORMATE,
  DEFAULT_VIEW_BILL_FILE_NAME,
  FILENAME_REGEX,
  localesWithDateFirst,
  localeDateFormats,
  PLATINUM_CORE_CODE,
  PLATINUM_ACHIEVER_LEVELTYPE,
  PLATINUM_REFERRRAL_LEVELTYPE,
  PLATINUM_LIFETIME_ELITE_LEVELTYPE,
  PLATINUM_PREMIER_PFS_LEVELTYPE,
  PLATINUM_PREMIER_PFR_LEVELTYPE,
  PLATINUM_PREMIER_ELITE_AMBASSADOR_LEVELTYPES,
  PLATINUM_PREMIER_ELITE_LEVELTYPE,
  MOB_NUM_MIN_LENGTH,
  AllLocalesKeysListConstant,
  DOT_SYMBOL,
  oneClickJoinRandDecryptURL,
  SYSTEM_MAINTENANCE_ERROR_URL,
  accountConstants,
  OPTED_OUT_PERSONALIZATION_KEY,
  ENROLLMENT_US_CONSENT_ENUM,
  ENROLLMENT_CONSTANTS,
  EDIT_CHANGE_PWRD_ID,
  BANNER_MSGS_KEYS,
  EDIT_PERSONAL_INFO_ID,
  TWO_STEP_VERIFICATION_ID,
  CreditCardType,
  ONE_CLICK_JOIN_MODAL_TYPE,
  oneClickJoinRandDecryptURLDtt,
  FORGOT_PWRD_ID,
  MEMBER_STATUS_LIST_LEVEL_HIERARCHY,
  MEMBERSHIP_CODES,
  CORE_LEVEL_CODES,
  CHINESE_MOBILE_LENGTH,
  CHINESE_MOBILE_CODE,
  MEMBERSHIP_LIFETIME_CODES_MAPPING,
  CN_CONTACT_MODES,
  NON_SUPPORTED_PHONE_NUMBER,
  FALLBACK_MONTHS_SHORT,
  FALLBACK_WEEKDAYS,
  FALLBACK_WEEKDAYS_SHORT,
  FALLBACK_MONTHS,
} from './constants';
import {
  canadaZipcodeRegex,
  canadaZipcodeWithSpaceRegex,
  emailRegex,
  lengthValidationRegex,
  usZipcodeRegex,
  nameFieldRegex,
  chineseCharRegex,
  httpRegex,
  htmlTagRegex,
  leadingSlashRegex,
  trailingExtensionRegex,
  slashGlobalRegex,
  dayjsFormatSplitRegex,
  dayjsEscapeBracketsRegex,
} from './regexUtils';
import { ACCOUNT_MODAL_VARIATION } from './accountModalEvents';
import { replaceText } from './promotionHelper';

declare global {
  interface String {
    getSymbol(): string;
  }
}

export const canUseDOM = !!(typeof window !== 'undefined' && window.document);

// ---------------------------------------------------------------------------
// Custom locale data store – populated by getCalednarLocaleKeys from CMS i18n
// Falls back to English values until the component initialises locale data.
// ---------------------------------------------------------------------------

const customLocaleData = {
  months: FALLBACK_MONTHS as string[],
  monthsShort: FALLBACK_MONTHS_SHORT as string[],
  weekdays: FALLBACK_WEEKDAYS as string[],
  weekdaysShort: FALLBACK_WEEKDAYS_SHORT as string[],
};

/**
 * Format a dayjs date using CMS-provided locale names for MMMM/MMM/dddd/ddd tokens.
 * All other tokens are handled natively by dayjs, avoiding the moment.js locale system.
 */
function applyCustomLocale(date: dayjs.Dayjs, format: string): string {
  const month = date.month(); // 0-based
  const dayOfWeek = date.day(); // 0-based, Sunday = 0
  return format
    .split(dayjsFormatSplitRegex)
    .map(token => {
      if (token === 'MMMM') return customLocaleData.months[month] ?? date.format('MMMM');
      if (token === 'MMM') return customLocaleData.monthsShort[month] ?? date.format('MMM');
      if (token === 'dddd') return customLocaleData.weekdays[dayOfWeek] ?? date.format('dddd');
      if (token === 'ddd') return customLocaleData.weekdaysShort[dayOfWeek] ?? date.format('ddd');
      if (dayjsEscapeBracketsRegex.test(token)) return token.slice(1, -1); // Strip dayjs escape brackets
      return token ? date.format(token) : '';
    })
    .join('');
}

/**
 * Get session data from PageContext with fallback to window.sessionStorage
 * @param pageContextSessionData - sessionData from PageContext (pageContext?.sessionData)
 * @returns Full sessionData object or null
 */
export const getSessionDataWithFallback = (pageContextSessionData?: any): any => {
  // First try to get from pageContext
  if (pageContextSessionData) {
    return pageContextSessionData;
  }

  // Fallback to window.sessionStorage
  if (canUseDOM) {
    try {
      const storedData = getWindowSession();
      if (storedData) {
        return storedData;
      }
    } catch (e) {
      console.error('Failed to parse sessionData from sessionStorage:', e);
    }
  }

  return null;
};

export const useGetBreakpoint = (): string | undefined => {
  if (typeof window !== 'undefined' && window?.screen && window?.screen?.width) {
    return window.innerWidth > BREAKPOINT_DESKTOP
      ? BREAKPOINT_DESKTOP_TEXT
      : window.innerWidth > BREAKPOINT_TABLET
      ? BREAKPOINT_TABLET_TEXT
      : BREAKPOINT_MOBILE_TEXT;
  } else {
    return undefined;
  }
};

// Plain function equivalent of useGetBreakpoint (same logic, no React hooks).
// Use this inside event handlers/effects where hooks are not allowed.
export const getBreakpoint = (): string | undefined => {
  if (typeof window !== 'undefined' && window?.screen && window?.screen?.width) {
    return window.innerWidth > BREAKPOINT_DESKTOP
      ? BREAKPOINT_DESKTOP_TEXT
      : window.innerWidth > BREAKPOINT_TABLET
      ? BREAKPOINT_TABLET_TEXT
      : BREAKPOINT_MOBILE_TEXT;
  } else {
    return undefined;
  }
};

export const getClassForBreakpoint = (
  breakpoint: string | undefined,
  desktopClass: string,
  tabletClass: string,
  mobileClass: string
): string => {
  if (!breakpoint) return desktopClass;
  if (desktopClass.indexOf(' ') > -1 || tabletClass.indexOf(' ') > -1 || mobileClass.indexOf(' ') > -1) {
    console.error(
      'Improper use of getClassForBreakpoint utility: To avoid mixed-purpose class assignment logic, please pass one class per breakpoint.'
    );
  }
  if (breakpoint === 'tablet') {
    return tabletClass;
  } else if (breakpoint === 'mobile') {
    return mobileClass;
  } else return desktopClass;
};

export function getScreenCategory() {
  let screenCategory = '';
  if (window.innerWidth < 240) {
    screenCategory = 'XXS';
  } else if (window.innerWidth >= 240 && window.innerWidth < 320) {
    screenCategory = 'XS';
  } else if (window.innerWidth >= 320 && window.innerWidth < 480) {
    screenCategory = 'S';
  } else if (window.innerWidth >= 480 && window.innerWidth < 560) {
    screenCategory = 'SM';
  } else if (window.innerWidth >= 560 && window.innerWidth < 672) {
    screenCategory = 'M';
  } else if (window.innerWidth >= 672 && window.innerWidth < 768) {
    screenCategory = 'MML';
  } else if (window.innerWidth >= 768 && window.innerWidth < 864) {
    screenCategory = 'ML';
  } else if (window.innerWidth >= 864 && window.innerWidth < 960) {
    screenCategory = 'MLL';
  } else if (window.innerWidth >= 960 && window.innerWidth < 1024) {
    screenCategory = 'L';
  } else if (window.innerWidth >= 1024 && window.innerWidth < 1200) {
    screenCategory = 'XL';
  } else if (window.innerWidth >= 1200 && window.innerWidth < 1440) {
    screenCategory = 'XXL';
  } else if (window.innerWidth >= 1440) {
    screenCategory = 'XXXL';
  } else {
    screenCategory = 'Unknown';
  }

  return screenCategory;
}

// updated datalyer props from clientside
export function UpdateDatalayerObj(dataLayer: Record<string, unknown>) {
  delete dataLayer['page_data_layer_ready'];

  let deviceOrientation = DATA_LAYER_PROPERTIES?.NEXT_PUBLIC_ORIENTATION_LANDSCAPE;
  const screenCategory = getScreenCategory();

  if (window.innerWidth < 576 && window.innerHeight > window.innerWidth) {
    deviceOrientation = DATA_LAYER_PROPERTIES?.NEXT_PUBLIC_ORIENTATION_PORTRAIT;
  }

  dataLayer[
    'page_breakpt_orientation'
  ] = `${DATA_LAYER_PROPERTIES?.NEXT_PUBLIC_PAGE_BREAKPT}${screenCategory} - ${window.innerWidth}w: ${deviceOrientation}`;

  dataLayer['previous_page'] = window.document?.referrer;
}

// Return Member Level Colors by levelTypeCode
export function getColorByCode(colorCode: string, isBackground: boolean, isMember: boolean = false) {
  // adding constant as there is no global reference for this colorcode
  const newMember = '#e6e6e6';

  switch (colorCode) {
    case 'P':
    case 'PAC':
    case 'PAR':
    case 'PLR':
      return `${baseVariables.color['platinumSolid']} !important`;
    case 'PPR':
    case 'PPC':
    case 'PL7':
    case 'PPE':
    case 'T':
      return `${baseVariables.color['titaniumSolid']} !important`;
    case 'G':
    case 'GAC':
    case 'GAR':
    case 'GLR':
      return `${baseVariables.color['goldSolid']} !important`;
    case 'S':
    case 'SAR':
    case 'SAC':
    case 'SLR':
      return `${baseVariables.color['silverSolid']} !important`;
    case 'A':
    case 'PFS':
    case 'PFR':
    case 'PPAE':
      return `${baseVariables.color['ambassadorSolid']} !important`;

    default:
      return isBackground
        ? `${newMember} !important`
        : isMember
        ? `${baseVariables.color['memberSolid']} !important`
        : `${baseVariables.color['base10']} !important`;
  }
}

// get colorcodes by mebership level
export function getGradientByCode(colorCode: string | null | undefined, isOverview?: boolean) {
  const rtlSuffix = isRtlDirection() ? 'Rtl' : '';
  switch (colorCode) {
    case 'P':
    case 'PAC':
    case 'PAR':
    case 'PLR':
      return `${baseVariables.color['platinumGradient' + rtlSuffix]}`;
    case 'PPR':
    case 'PPC':
    case 'PL7':
    case 'PPE':
    case 'T':
      return `${baseVariables.color['titaniumGradient' + rtlSuffix]} !important`;
    case 'G':
    case 'GAC':
    case 'GAR':
    case 'GLR':
      return `${baseVariables.color['goldGradient' + rtlSuffix]} !important`;
    case 'S':
    case 'SAR':
    case 'SAC':
    case 'SLR':
      return `${baseVariables.color['silverGradient' + rtlSuffix]} !important`;
    case 'A':
    case 'PFS':
    case 'PFR':
    case 'PPAE':
      return `${baseVariables.color['ambassadorGradient' + rtlSuffix]} !important`;

    default:
      return `${
        isOverview ? baseVariables.color['base20'] : baseVariables.color['memberGradient' + rtlSuffix]
      } !important`;
  }
}

export function getGradientByCodeV2(colorCode: string | null | undefined, isOverview?: boolean) {
  const rtlSuffix = isRtlDirection();
  const pick = (ltr: string, rtl: string) => (rtlSuffix ? rtl : ltr);
  switch (colorCode) {
    case 'P':
    case 'PAC':
    case 'PAR':
    case 'PLR':
      return pick(
        `linear-gradient(64deg, #43454A 7.01%, #606267 51.41%, #A9A5A5 93.11%)`, // LTR (existing)
        `linear-gradient(244deg, #43454A 92.99%, #606267 51.41%, #A9A5A5 93.11%)` // RTL (new)
      );
    case 'PPR':
    case 'PPC':
    case 'PL7':
    case 'PPE':
    case 'T':
      return pick(
        `linear-gradient(64deg, #29141D 7.01%, #431E2E 51.41%, #635154 93.11%) !important`, // LTR (existing)
        `linear-gradient(244deg, #29141D 92.99%, #431E2E 51.41%, #635154 93.11%) !important` // RTL (new)
      );
    case 'G':
    case 'GAC':
    case 'GAR':
    case 'GLR':
      return pick(
        `linear-gradient(64deg, #6F4818 7.01%, #87673E 51.41%, #DFB47D 93.11%) !important`, // LTR (existing)
        `linear-gradient(244deg, #6F4818 92.99%, #87673E 51.41%, #DFB47D 93.11%) !important` // RTL (new)
      );
    case 'S':
    case 'SAR':
    case 'SAC':
    case 'SLR':
      return pick(
        `linear-gradient(64deg, #575B6E 7.01%, #70758D 51.41%, #99A3CF 93.11%) !important`, // LTR (existing)
        `linear-gradient(244deg, #575B6E 92.99%, #70758D 51.41%, #99A3CF 93.11%) !important` // RTL (new)
      );
    case 'A':
    case 'PFS':
    case 'PFR':
    case 'PPAE':
      return pick(
        `linear-gradient(64deg, #1F1D20 7.01%, #3C3B3F 51.41%, #7D7C83 93.11%) !important`, // LTR (existing)
        `linear-gradient(244deg, #1F1D20 92.99%, #3C3B3F 51.41%, #7D7C83 93.11%) !important` // RTL (new)
      );

    default:
      return pick(
        isOverview
          ? baseVariables.color['base20']
          : `linear-gradient(64deg, #A4451D 7.01%, #C05721 51.41%, #FF965F 93.11%) !important`,
        isOverview
          ? baseVariables.color['base20']
          : `linear-gradient(244deg, #A4451D 92.99%, #C05721 51.41%, #FF965F 93.11%) !important`
      );
  }
}

// function returns the text color base on member status
export function getTextColorByCode(colorCode: string | undefined) {
  if (colorCode === null || colorCode === undefined || colorCode === 'R') {
    return `${baseVariables.color['base10']}`;
  } else {
    return `${baseVariables.color['base20']}`;
  }
}
//to get range of date
export const formatDateRange = (start: any, end: any, currentLocale: string) => {
  if (start && end) {
    const stratDateObj = getDateObjectCurrent(start);
    const endDateObj = getDateObjectCurrent(end);
    const startYear = stratDateObj.year();
    const endYear = endDateObj.year();
    const startMonth = stratDateObj.month();
    const endMonth = endDateObj.month();
    const locale = currentLocale?.replace('_', '-') ?? 'en-US';
    let localeDateFormat = localeDateFormats[locale]?.fullDate || localeDateFormats['default']?.fullDate;

    if (startYear === endYear) {
      if (startMonth === endMonth) {
        let leftDateFormat;
        let rightDateFormat;
        if (localesWithDateFirst?.includes(locale)) {
          leftDateFormat = localeDateFormats[locale]?.onlyDate;
          rightDateFormat = localeDateFormats[locale]?.excludeYear;
        } else {
          leftDateFormat = localeDateFormats[locale]?.excludeYear;
          rightDateFormat = localeDateFormats[locale]?.onlyDate;
        }

        return `${stratDateObj.format(leftDateFormat)}  -  ${endDateObj.format(rightDateFormat)}`;
      } else {
        localeDateFormat = localeDateFormats[locale]?.excludeYear;
      }
    } else {
      localeDateFormat = localeDateFormats[locale]?.fullDate;
    }
    return `${stratDateObj.format(localeDateFormat)}  -  ${endDateObj.format(localeDateFormat)}`;
  }
  return '';
};

export function formatDate(
  inputDateStr: string | undefined,
  currentLocale: string | undefined,
  isOverViewPage?: boolean
) {
  // Null check for inputDateStr
  if (!inputDateStr) return '';
  const dateObj = getDateObject(inputDateStr);
  const locale = currentLocale?.replace('_', '-')?.toLowerCase();
  if (locale === constants?.GERMAN_LOCALE) {
    // German locale format: dd.mm.yyyy
    return getLocalizedDate(dateObj, DATE_FORMATE?.GERMAN_DATE_FORMAT);
  } else {
    // Default format: Month dd, yyyy
    if (isOverViewPage) {
      if (locale === constants?.US_LOCALE) {
        return getLocalizedDate(dateObj, DATE_FORMATE?.ENGLISH_FULLMONTH_FORMAT);
      } else {
        return convertDateToLocaleSpecific(inputDateStr, currentLocale);
      }
    } else {
      return getLocalizedDate(dateObj, DATE_FORMATE?.ENGLISH_DATE_FORMAT);
    }
  }
}

//generate ritz-carlton HWS url.
export const generateRitzCarltonHotelUrl = (propertyId: string, propertyName: any, currentLocale: string) => {
  const propertyUrlName = propertyName
    ?.toLowerCase()
    .replace('-', ' ')
    .replace(/([~!@#$%^&*()_+=`{}[\]|\\:;'<>,./? ])+/g, '-')
    .replace(/^(-)+|(-)+$/g, '');
  if (currentLocale === 'en_US' || currentLocale === 'en_GB') {
    currentLocale = currentLocale.replace('_', '-');
  } else {
    currentLocale = currentLocale?.split('-')?.[0];
  }
  const url = RITZ_CARLTON_URl?.replace(
    '{currentLocale}',
    SUBDIRECTORY_PREFIX?.toLocaleLowerCase() || currentLocale?.toLocaleLowerCase()
  )
    ?.replace('{propertyId}', propertyId?.toLowerCase())
    ?.replace('{propertyUrlName}', propertyUrlName);
  return url;
};
//generate HWS url.
export const generateViewHotelUrl = (propertyId: string, propertyName: any) => {
  const propertyUrlName = propertyName
    ?.toLowerCase()
    .replace('-', ' ')
    .replace(/([~!@#$%^&*()_+=`{}[\]|\\:;'<>,./? ])+/g, '-')
    .replace(/^(-)+|(-)+$/g, '');
  const url = HWS_RESERVATION_URL?.replace('{propertyId}', propertyId?.toLowerCase())?.replace(
    '{propertyUrlName}',
    propertyUrlName?.toLowerCase()
  );
  return addSubDirectoryPrefix(url);
};

// generate masked string
export const maskEmail = (email: string | undefined | null) => {
  if (!email || email?.trim() === '') {
    return ' ';
  }
  const [localPart, domain] = email.split('@');
  const maskedLocalPart = localPart?.slice(0, 2) + '*'?.repeat(localPart?.length - 4) + localPart?.slice(-2);
  return `${maskedLocalPart}@${domain}`;
};

/**
 *
 * @param url as str
 * This is used find the nth occurence of a char in string
 */
const findNthOccurence = function (str: string, nth: number, char: string) {
  let index = 0;
  for (let i = 0; i < nth; i += 1) {
    if (index !== -1) index = str.indexOf(char, index + 1);
  }
  return index;
};

/**
 *
 * @param prefix
 * This is used for setting the prefix value to the variable from Page level attribute in model.json.
 */
let SUBDIRECTORY_PREFIX = '';
export function setSubDirectoryPrefix(prefix = '') {
  SUBDIRECTORY_PREFIX = prefix;
}
/**
 *
 * @param hwsUrl
 * This is used for setting the hwsUrl value to the variable from Page level attribute in model.json.
 */
let HWS_RESERVATION_URL = '';
export function setHWSReservationURL(hwsUrl = '') {
  HWS_RESERVATION_URL = hwsUrl;
}
let RITZ_CARLTON_URl = '';
export function setRitzCarltonUrl(url = '') {
  RITZ_CARLTON_URl = url;
}
/**
 *
 * @param url
 * This is used for prefix the url. This will check for subdirectory prefix
 * if present then it will be prefixed otherwise the url is returned without prefix.
 */
export const addSubDirectoryPrefix = function (url = '') {
  SUBDIRECTORY_PREFIX =
    SUBDIRECTORY_PREFIX || (canUseDOM ? window?.sessionStorage?.getItem('SUBDIRECTORYCONTEXTPATH') : '') || '';
  if (
    SUBDIRECTORY_PREFIX &&
    SUBDIRECTORY_PREFIX.toLowerCase() !== 'en-us' &&
    url?.startsWith('/') &&
    url?.substring(1, findNthOccurence(url, 1, '/'))?.toLowerCase() !== SUBDIRECTORY_PREFIX?.toLowerCase()
  ) {
    return SUBDIRECTORY_PREFIX ? '/' + SUBDIRECTORY_PREFIX + url : url;
  } else return url;
};

export const getCustomClickTrackingPageName = (trackingPageName?: string, fallbackPathname?: string): string => {
  if (trackingPageName) {
    return trackingPageName;
  }

  const pathnameToUse = typeof window !== 'undefined' ? window.location.pathname : fallbackPathname ?? '';

  if (!pathnameToUse) {
    return '';
  }

  const underscoredPathname = pathnameToUse
    .trim()
    .replace(leadingSlashRegex, '')
    .replace(trailingExtensionRegex, '')
    .replace(slashGlobalRegex, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();

  return underscoredPathname;
};

export const addLocalePrefix = function (sourceUrl: string, targetUrl: string) {
  const localeString = sourceUrl?.substring(1, findNthOccurence(sourceUrl, 1, '/')) || '';
  if (localeString.length === 2 || (localeString.length === 5 && localeString.charAt(2) === '-')) {
    return '/' + localeString + targetUrl;
  } else return targetUrl;
};

// generate apollo clientHeaders
export const generateApolloClientHeaders = (
  isLocalDev: boolean,
  pageContext: any,
  extraHeaders: { [key: string]: string | boolean | ((...arg: string[]) => void) } = {}
) => {
  const currentLocale = pageContext?.currentLocale ?? 'en_US';
  const accessToken = pageContext?.sessionData?.cacheData?.data?.accessToken ?? pageContext?.accessToken;

  const context: any = {
    headers: {
      'accept-language': currentLocale?.replace('_', '-'),
      ...extraHeaders,
    },
  };

  if (isLocalDev && accessToken) {
    context.headers = {
      ...context.headers,
      Authorization: `Bearer ${accessToken}`,
    };
  }

  return context;
};

export const checkUSDomainFuntion = (currentLocale: string) => {
  const localeToCheck = currentLocale?.replace('_', '-');
  return localeToCheck === AllLocalesKeysListConstant.en;
};

//suite nigh awards status check
export function getStatusMessage(code: any, requestedMsg: any, deniedMsg: any, upgradedMsg: any) {
  switch (code) {
    case SNA_Status_REQUESTED:
      return requestedMsg;
    case SNA_Status_GUARANTEED:
      return upgradedMsg;
    case SNA_Status_DENIED:
      return deniedMsg;
    default:
      return null;
  }
}

// convert locale string to {countryCode: '', langCode: ''}
// This will take the current locale string as parameter and return a object with [countryCode] and [langCode] as a key
export const getCountryAndLangCode = (input: string) => {
  let currentLocaleArr;
  if (input?.includes('_')) currentLocaleArr = input?.split('_');
  if (input?.includes('-')) currentLocaleArr = input?.split('-');
  return {
    countryCode: currentLocaleArr && currentLocaleArr[1],
    langCode: currentLocaleArr && currentLocaleArr[0],
  };
};

// check if locale is set to china
// This will take the current locale string as parameter and return a boolean based on country code(China=CN) condition check
export const checkChinaLocale = (currLocale: string) => {
  const { countryCode } = getCountryAndLangCode(currLocale);
  return countryCode?.toUpperCase() === constants.CHINA_COUNTRY_CODE;
};

export const checkUSLocale = (currLocale: string) => {
  const { countryCode } = getCountryAndLangCode(currLocale);
  return countryCode?.toUpperCase() === constants.USA_COUNTRY_CODE;
};

export const checkKOLocale = (currLocale: string) => {
  const { countryCode } = getCountryAndLangCode(currLocale);
  return countryCode?.toUpperCase() === constants.KOREA_COUNTRY_CODE;
};

export const checkAELocale = (currLocale: string) => {
  const { countryCode } = getCountryAndLangCode(currLocale);
  return countryCode?.toUpperCase() === constants.ARABIC_COUNTRY_CODE;
};

export const checkChinaTWLocale = (currLocale: string) => {
  const { countryCode } = getCountryAndLangCode(currLocale);
  return countryCode?.toUpperCase() === constants.TRADITIONAL_CHINA_COUNTRY_CODE; //zh-HK locale is recieved as zh-TW locale, thus using the same
};

export const checkGreaterChinaLocale = (currLocale: string) => {
  return currLocale && (checkChinaLocale(currLocale) || checkChinaTWLocale(currLocale));
};

export const getCalednarLocaleKeys = (data: any) => {
  /** required value for calendar
   * these value updates as per locale
   * we will get week days infromation from AEM
   * update AEM value and fallback
   */
  // add new split parameter for TC site. the AEM value on the TC site is divided by '、'
  let shortWeekdays: Array<string> = data?.shortWeekDays?.replaceAll(' ', '')?.split(/,|،|、/);
  // set default weekdays if i18 weekdays value are invalid
  if (shortWeekdays?.length !== 7) {
    shortWeekdays = calendarConstants.SHORT_WEEK_DAYS;
  }

  let shortWeekDaysName: Array<string> = data?.shortWeekDaysName?.replaceAll(' ', '')?.split(/,|،|、/);
  // set default weekdays if i18 weekdays value are invalid
  if (shortWeekDaysName?.length !== 7) {
    /** fallback value if we are not getting this details from AEM */
    shortWeekDaysName = calendarConstants.SHORT_WEEK_DAYS_NAME;
  }

  let longWeekDaysName: Array<string> = data?.longWeekDaysName?.replaceAll(' ', '')?.split(/,|،|、/);
  if (longWeekDaysName?.length !== 7) {
    /** fallback value if we are not getting this details from AEM */
    longWeekDaysName = calendarConstants.LONG_WEEK_DAYS_NAME;
  }

  let longMonthName: Array<string> = data?.longMonthName?.replaceAll(' ', '')?.split(/,|،|、/);
  // set default month if i18 months value are invalid
  if (longMonthName?.length !== 12) {
    longMonthName = FALLBACK_MONTHS;
  }

  let shortMonthName: Array<string> = data?.shortMonthName?.replaceAll(' ', '')?.split(/,|،|、/);
  // set default month if i18 months value are invalid
  if (shortMonthName?.length !== 12) {
    shortMonthName = calendarConstants.SHORT_MONTH_NAME;
  }

  // Update module-level locale data used by convertDateToLocaleSpecific
  customLocaleData.months = longMonthName;
  customLocaleData.monthsShort = shortMonthName;
  customLocaleData.weekdays = longWeekDaysName;
  customLocaleData.weekdaysShort = shortWeekDaysName;
  return {
    shortWeekdays,
    shortWeekDaysName,
    longWeekDaysName,
    longMonthName,
    shortMonthName,
  };
};

// add arrow to anchor tag in rte label
export function addClassToBlankTargetLinks(
  inputString: string,
  inputTarget: string = '_blank',
  prefixUrl?: string
): string {
  if (canUseDOM) {
    //TO DO:- To remove dom parser
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(inputString, 'text/html');

    // Find all <a> tags with target="_blank"
    const targetInput = `a[target='${inputTarget}']${inputTarget !== '_blank' ? `, a[target='_blank']` : ''}`;

    const blankTargetLinks = parsedDocument.querySelectorAll(targetInput);

    blankTargetLinks.forEach(link => {
      if (inputTarget !== '_blank') {
        link.setAttribute('target', '_blank');
      }

      const arrowIcon = parsedDocument.createElement('span');
      arrowIcon.classList.add('icon-external-arrow'); // Add your arrow class here
      // Append the arrow icon to the anchor tag
      link.appendChild(arrowIcon);
    });

    //fetch all anchor tags in the rich text
    if (prefixUrl) {
      const anchorTags = parsedDocument.querySelectorAll('a');
      anchorTags?.forEach(link => {
        const url = link?.getAttribute('href');
        // check if http or https is already present to url
        if (url && !httpRegex.test(url)) {
          const hrefUrl = `${prefixUrl}${url}`;
          link.setAttribute('href', hrefUrl);
        }
      });
    }

    // Serialize the modified DOM back to a string
    const modifiedString = new XMLSerializer().serializeToString(parsedDocument);

    return modifiedString;
  } else {
    return inputString;
  }
}

// function to get the memberStatusDescription based on levelCode
export function getMemberStatusDescription(
  levelCode: string | undefined,
  memberStatusList: Array<{ id: string; label: string }>,
  lifetimLevelCode: string | null | undefined = null
) {
  if (lifetimLevelCode && MEMBER_STATUS_LIST_LEVEL_HIERARCHY?.[lifetimLevelCode]?.includes(levelCode || '')) {
    const memberStatusDescription = memberStatusList?.filter(
      memberStatusData => memberStatusData.id === lifetimLevelCode
    );
    return memberStatusDescription?.[0]?.label ?? '';
  } else {
    const memberStatusDescription = memberStatusList?.filter(memberStatusData => memberStatusData.id === levelCode);
    return memberStatusDescription?.[0]?.label ?? '';
  }
}

export function getFileNameFromContentDisposition(contentDisposition: string) {
  const filenameRegex = FILENAME_REGEX;
  const matches = filenameRegex.exec(contentDisposition);
  if (matches != null && matches[1]) {
    return matches[1].replace(/['"]/g, '');
  }
  return DEFAULT_VIEW_BILL_FILE_NAME;
}

export function convertDateToLocaleSpecific(date?: string, locale?: string, dateFormat?: string): string {
  if (date) {
    const dateObj = dayjs(date);
    const localeDateFormat = dateFormat ?? localeDateFormats[locale?.replace('_', '-') ?? 'en-US']?.fullDate;
    const formatted = applyCustomLocale(dateObj, localeDateFormat);
    if (getLanguageFromDocumentLang() === constants.THAI_LANG) {
      return modifyDateStringForThai(formatted);
    }
    return formatted;
  }
  return '';
}

export function getMemberLevel(levelCode: string | undefined, levelTypeCode: string) {
  if (levelCode?.toUpperCase() === PLATINUM_CORE_CODE && levelTypeCode) {
    switch (levelTypeCode?.toUpperCase()) {
      case PLATINUM_ACHIEVER_LEVELTYPE:
      case PLATINUM_REFERRRAL_LEVELTYPE:
      case PLATINUM_LIFETIME_ELITE_LEVELTYPE:
        return PLATINUM_CORE_CODE;
      case PLATINUM_PREMIER_PFS_LEVELTYPE:
      case PLATINUM_PREMIER_PFR_LEVELTYPE:
        return PLATINUM_PREMIER_ELITE_AMBASSADOR_LEVELTYPES;
      default:
        return PLATINUM_PREMIER_ELITE_LEVELTYPE;
    }
  }
  return levelCode;
}

export function getMemberLevelV2(levelCode: string | undefined, levelTypeCode: string) {
  if (!levelCode || !levelTypeCode) {
    return levelCode;
  }

  const upperLevelCode = levelCode.toUpperCase();
  const upperLevelTypeCode = levelTypeCode.toUpperCase();

  // Handle Titanium tiers - check levelTypeCode first as it's more specific
  if (MEMBERSHIP_CODES.TITANIUM_MEMBER_CODES.includes(upperLevelTypeCode)) {
    return 'PPC'; // Titanium Elite
  }

  // Handle Ambassador tiers - check levelTypeCode first as it's more specific
  if (MEMBERSHIP_CODES.AMBASSADOR_MEMBER_CODES.includes(upperLevelTypeCode)) {
    return 'PFS'; // Ambassador Elite
  }

  // Handle Platinum-based tiers
  if (upperLevelCode === PLATINUM_CORE_CODE) {
    return 'P'; // Platinum Elite - keep it simple like Gold and Silver
  }

  // Handle Gold-based tiers
  if (upperLevelCode === 'G') {
    return 'G'; // Gold Elite
  }

  // Handle Silver-based tiers
  if (upperLevelCode === 'S') {
    return 'S'; // Silver Elite
  }

  // Handle Gold tiers
  if (MEMBERSHIP_CODES.GOLD_MEMBER_CODES.includes(upperLevelTypeCode)) {
    return 'G'; // Gold Elite
  }

  // Handle Silver tiers
  if (MEMBERSHIP_CODES.SLIVER_MEMBER_CODES.includes(upperLevelTypeCode)) {
    return 'S'; // Silver Elite
  }

  // Handle Platinum tiers
  if (MEMBERSHIP_CODES.PLATINUM_MEMBER_CODES.includes(upperLevelTypeCode)) {
    return 'P'; // Platinum Elite
  }

  // For other cases, return the levelTypeCode if it's a valid tier code
  if (CORE_LEVEL_CODES.includes(upperLevelTypeCode)) {
    return upperLevelTypeCode; // Valid tier code (R, S, G, P, PPC, PFS)
  }

  // Fallback to levelCode
  return levelCode; // Original level code as fallback
}

export const memberLink = (code: string | undefined) => {
  switch (code) {
    case 'P':
      return 'platinum';
    case 'S':
      return 'silver';
    case 'G':
      return 'gold';
    case 'PPAE':
    case 'A':
      return 'ambassador';
    case 'PPE':
    case 'PPR':
    case 'PPC':
    case 'PL7':
    case 'T':
      return 'titanium';
    case 'M':
      return 'member';
    default:
      return 'member';
  }
};

/**
 * add sub directory prefixes to the header urls if they don't have it
 */

export const correctSubDirectoryPathForHeader = () => {
  /**
   * add subdirectory path if path is not there
   */
  const headerChildArr =
    (document.querySelector('header')?.querySelector('nav')?.querySelector('ul')?.children as any) || []; //access header link on page
  Array.from(headerChildArr)?.forEach((el: any) => {
    const anchor = el.querySelector('a');
    const tripDataVal = anchor?.dataset?.authenticatedUrl;
    const overlayEndpoint = anchor?.dataset?.overlayEndpoint;
    const urlString: string = anchor?.attributes?.href?.value;
    if (urlString !== '#' && !urlString.startsWith('https')) {
      anchor.setAttribute('href', addSubDirectoryPrefix(urlString));
    }
    if (tripDataVal) {
      anchor.dataset.authenticatedUrl = addSubDirectoryPrefix(tripDataVal);
    }
    if (overlayEndpoint) {
      anchor.dataset.overlayEndpoint = addSubDirectoryPrefix(overlayEndpoint);
    }
  });
};

export const getLocaleFromParams = (resolvedUrlString: string) => {
  const resolvedUrlParams = resolvedUrlString?.split('?')[1];
  const paramsArr = resolvedUrlParams?.split('&');
  let paramLocale;
  paramsArr?.forEach(param => {
    if (param.includes('locale=')) {
      paramLocale = param.split('=')[1];
    }
  });
  return paramLocale;
};

/**
 * getCurrentUrlParams() should return search parameters by reading current url from window.location
 */
export const getCurrentUrlParams = (isOverlay?: boolean) => {
  return isOverlay ? window?.parent?.location?.search : window?.location?.search;
};
/**
 * end of the code
 */

export const encryptPassword = (password: string, publicKey: string) => {
  if (publicKey && publicKey !== null) {
    const key = forge.pki.publicKeyFromPem(publicKey);

    const encryptedPass = key.encrypt(password, 'RSA-OAEP', {
      md: forge.md.sha256.create(),
      mgf1: {
        md: forge.md.sha256.create(),
      },
    });

    return forge.util.encode64(encryptedPass);
  } else {
    return password;
  }
};

export const getSubDirectoryPrefixedUrl = (url: string, currLocale: string): string => {
  const isChinaLocale = checkChinaLocale(currLocale);
  if (!isChinaLocale) {
    return addSubDirectoryPrefix(url);
  }
  return url;
};

/**
 * truncate text if length exceeds maxLength and append with ellipsis
 */
export const truncateText = (text: string, maxLength: number) => {
  if (!text) {
    return '';
  }
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + '...';
  } else {
    return text;
  }
};

/**
 * Fisher–Yates shuffle that returns a new array.
 * Optional RNG for deterministic testing.
 */
export function shuffle<T>(arr: T[], rng: () => number = Math.random): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export const getRedirectUrlFromParams = (isEAASignIn: boolean, isOverlay: boolean, skipDecodeForPaths?: string) => {
  const urlParam = getCurrentUrlParams(isOverlay);
  const param = new URLSearchParams(urlParam);
  const returnToUrl = param.get(isEAASignIn ? 'redirect_uri' : 'returnTo');
  const shouldSkipDecode = skipDecode(returnToUrl, skipDecodeForPaths);
  return returnToUrl ? (shouldSkipDecode ? returnToUrl : decodeURIComponent(returnToUrl)) : null;
};

export const skipDecode = (redirectUrl: string | null, skipPaths: string | undefined) => {
  const paths = skipPaths?.split(',') ?? [];
  let decode = false;
  if (paths.length > 0) {
    decode = paths?.some(path => redirectUrl?.includes(path));
  }
  return decode;
};

// This function takes a mobile num as string and checks for mobile number's minimum length to determine its validity
export const checkMobileNumValidity = (mobNum: string | undefined) => {
  let isValid = true;
  const mobileNumberLength = mobNum?.trim()?.substring(3)?.length || 0;
  if (
    !mobNum ||
    (mobNum &&
      (mobNum?.includes(CHINESE_MOBILE_CODE)
        ? mobileNumberLength !== CHINESE_MOBILE_LENGTH
        : mobileNumberLength < MOB_NUM_MIN_LENGTH))
  ) {
    isValid = false;
  }
  return isValid;
};
// Validates a string value to check if it's null, undefined, or consists of only whitespace characters.
export const validateField = (value: string) => {
  return value === null || (typeof value === 'string' && value.trim().length === 0);
};

export const getMaskedDot = (isMobile?: boolean) => {
  const dots = DOT_SYMBOL.repeat(4);
  if (isMobile) {
    return dots; // for mobile need single set of 4 dots
  } else {
    return dots + ' ' + dots + ' ' + dots;
  }
};

export const isCardExpired = (expiration: { month: number; year: number }): boolean => {
  const month = expiration?.month;
  const year = expiration?.year;
  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth() + 1;
  return year < currentYear || (year === currentYear && month < currentMonth);
};

export const getNextPeriodnYears = (len: number, defaultLabel: string) => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: len }, (_, i) => ({
    id: `${currentYear + i}`,
    value: `${currentYear + i}`,
  }));
  return [{ id: '', value: defaultLabel }, ...years];
};
export const handleClickRte = (handleLinkClick: any, id: string) => {
  const rteLink = document.querySelector(id);
  rteLink?.addEventListener('click', handleLinkClick as EventListener);
};

export const randDecrpytAPI = async (randParameter?: string, isDTT?: boolean) => {
  return axios.get(isDTT ? oneClickJoinRandDecryptURLDtt : oneClickJoinRandDecryptURL, {
    params: {
      rand: randParameter,
    },
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const isReservationModal = (isAccountModal?: boolean, variation?: string) => {
  return (isAccountModal && variation === ACCOUNT_MODAL_VARIATION?.RESERVATION) ?? false;
};

export const isArraysEqual = (arr1: string[], arr2?: string[]) => {
  if (arr1?.length !== arr2?.length) {
    return false;
  }
  return arr1.every(value => arr2?.includes(value));
};
export const getErrorUrl = (errorType: string) => replaceText(SYSTEM_MAINTENANCE_ERROR_URL, [errorType]);

export const checkAndSetConsentChecked = (
  consentFeatures: { feature: { code: string }; value: string | null }[] | undefined,
  CONSENT_CODE: string,
  valueToCheck: string
): boolean => {
  const foundFeature = consentFeatures?.find(preference => preference?.feature.code === CONSENT_CODE);
  return foundFeature ? foundFeature?.value === valueToCheck : false;
};

//Checks if a given string contains Chinese characters.
export const isChinese = (str: string) => {
  return chineseCharRegex.test(str?.trim());
};

// prevents scrolling of the body when a modal/flyout is open
const modalStyles: Record<string, string> = {
  overflow: 'hidden',
  position: 'fixed',
  top: '0',
  left: '0',
  right: '0',
  width: '100%',
  'touch-action': 'none',
  'overscroll-behavior': 'none',
};

export const modalScrollHandler = (isModalOpen: boolean = false) => {
  if (!canUseDOM || !document.body) return;

  Object.entries(modalStyles).forEach(([property, value]) => {
    if (isModalOpen) {
      document.body.style.setProperty(property, value, 'important');
    } else {
      document.body.style.removeProperty(property);
    }
  });
};

export const getContentBlockImageDimensions = (breakpoint: 'lg' | 'md' | 'sm' | 'all', isOverlay = false) => {
  switch (breakpoint) {
    case 'lg':
      return accountConstants.CONTENT_BLOCK_IMAGE_WIDTH.lg;
    case 'md':
      return isOverlay ? accountConstants.CONTENT_BLOCK_IMAGE_WIDTH.lg : accountConstants.CONTENT_BLOCK_IMAGE_WIDTH.md;
    case 'sm':
      return isOverlay ? accountConstants.CONTENT_BLOCK_IMAGE_WIDTH.md : accountConstants.CONTENT_BLOCK_IMAGE_WIDTH.sm;
    default:
      return accountConstants.CONTENT_BLOCK_IMAGE_WIDTH.sm;
  }
};

// util to validate if the LHS and RHS countries are same
export const isCoutriesSame = (countryRHS: string, countryLHS?: string) => {
  return countryLHS && countryRHS && countryLHS === countryRHS;
};

export const checkForSpecialCharacters = (str: string): boolean => {
  const regex = /[^a-zA-Z0-9]/;
  return regex.test(str);
};

export function updateConsentFeatureOptOut(
  consentFeatures: ConsentType[] | undefined,
  consentChecked: boolean | undefined
) {
  const updatedConsentFeatures = consentFeatures?.map(feature => {
    if (feature.feature.code === OPTED_OUT_PERSONALIZATION_KEY) {
      return { ...feature, value: consentChecked ? 'N' : 'Y' };
    }
    return feature;
  });

  return updatedConsentFeatures;
}

// This method is used to fetch enrollment source code from the mapped list of enrollment source code based on locales.
//It accepts locale as input and extracts country code from it to generate enrollment source code key to fetches related enrollment source code
export const getEnrollmentSourceCodeFromCountry = (
  locale: string,
  sourceMap?: {
    id?: string;
    value?: string;
  }[]
) => {
  const localeCountryCode = getCountryAndLangCode(locale)?.countryCode?.toUpperCase();
  return getEnrollmentSourceCodeFromKey(
    localeCountryCode === ENROLLMENT_US_CONSENT_ENUM?.SP
      ? ENROLLMENT_CONSTANTS?.LACA
      : `${localeCountryCode}${ENROLLMENT_CONSTANTS?.SOURCE_CODE_KEY_SUFFIX}`,
    sourceMap
  );
};

// This method is used to fetch enrollment preferred language from the mapped list based on locale.
//It accepts locale as input and fetches the related preferred language code.
export const getEnrollmentPreferredLanguageCode = (
  locale?: string,
  sourceMap?: {
    id?: string;
    value?: string;
  }[]
) => {
  return sourceMap?.filter(item => item.id === locale)?.[0]?.value;
};

// This method is used to fetch enrollment source code from the mapped list of enrollment source code based on locales.
//It accepts enrollment Source Code Key as input and fetches related enrollment source code
export const getEnrollmentSourceCodeFromKey = (
  enrollmentSourceCodeKey?: string,
  sourceMap?: {
    id?: string;
    value?: string;
  }[]
) => {
  return sourceMap?.filter(item => item.id === enrollmentSourceCodeKey)?.[0]?.value;
};

export const handleClickRoomDetail = ({ e, id, item, key }: any) => {
  e?.preventDefault();
  eventUtil?.dispatch(key, {
    id,
    data: {
      roomData: item,
    },
  });
};
//get current timestamp
export function getCurrentTimestamp() {
  const date = new Date();
  // Convert it to an ISO string (e.g., "2024-05-10T10:22:04Z")
  const timestamp = date.toISOString();

  return timestamp;
}

export const getBannerMessages = (bannerId: string) => {
  switch (bannerId) {
    case FORGOT_PWRD_ID:
      return BANNER_MSGS_KEYS.PWRD_UPDATED_MSG;
    case EDIT_PERSONAL_INFO_ID:
      return BANNER_MSGS_KEYS.ACCOUNT_UPDATED_MSG;
    case TWO_STEP_VERIFICATION_ID:
      return BANNER_MSGS_KEYS.TWO_STEP_VERIFICATION_MSG;
    default:
      return '';
  }
};

export const getCardTypeCode = (cardType: string): string | undefined => {
  return Object.keys(CreditCardType).find(key => CreditCardType[key as keyof typeof CreditCardType] === cardType);
};

// This method is used to get redirect url for OCJ modals
// To get error Return url, we need to pass true as second parameter
export const getOCJRedirectUrl = (modalType?: string, getErrorUrl?: boolean, findReservationDetailCTAPath?: string) => {
  const isCheckedIn =
    modalType === ONE_CLICK_JOIN_MODAL_TYPE?.CHECKED_IN || modalType === ONE_CLICK_JOIN_MODAL_TYPE?.CHECKED_IN_V2;
  const isUpcoming = modalType === ONE_CLICK_JOIN_MODAL_TYPE?.UPCOMING;

  if (getErrorUrl) {
    return isCheckedIn
      ? constants?.CHECK_IN_CONFIRMATION
      : isUpcoming
      ? findReservationDetailCTAPath || constants?.FIND_RESERVATION_DETAIL
      : constants.CREATE_ACCOUNT_PAGE_URL;
  } else {
    return isUpcoming ? constants?.FIND_RESERVATION_DETAIL : constants.FIND_RESERVATION_LIST_PAGE_URL;
  }
};

//This method checks for:
// Valid US or Canada zipcode
export const checkForValidZipCode = (zipCode: string, countryCode: string) => {
  const trimmedZipcode = zipCode.trim();
  return (
    lengthValidationRegex.test(trimmedZipcode) &&
    ((countryCode === constants?.CANADA_COUNTRY_CODE &&
      (canadaZipcodeWithSpaceRegex.test(trimmedZipcode) || canadaZipcodeRegex.test(trimmedZipcode))) ||
      (countryCode === constants?.USA_COUNTRY_CODE && usZipcodeRegex.test(trimmedZipcode)))
  );
};

export function getWindowSession(): any {
  if (canUseDOM) {
    return window['sessionDataClient' as any] ?? window['sessionData'] ?? {};
  }
  return {};
}

export function loadLocaleCurrency(currencySymbol: string) {
  const sessionObject = getWindowSession();
  const localeCode = sessionObject?.locale?.split(/_|-/)?.[1];

  const rk_currency = sessionObject?.cacheData?.data?.AriesCommon?.rk_currency;
  const list: { [key: string]: string } = {};
  rk_currency?.split(',')?.forEach((pair: string) => {
    const [rk, currency] = pair.split(':');
    list[rk.trim()] = currency?.trim();
  });
  const localeCurrency = list[localeCode];

  // eslint-disable-next-line no-extend-native
  String.prototype.getSymbol = function () {
    const currency = String(this || '');
    const isLocaleCurrency = localeCurrency?.toUpperCase() === currency?.toUpperCase();
    return currencySymbol && isLocaleCurrency ? currencySymbol : currency;
  };
}
//check email formate function
export function isValidEmaiFormat(email: string): boolean {
  // Check if email matches the regex and has length between 5 and 80
  return emailRegex.test(email) && email.length >= 5 && email.length <= 80;
}

//This method use to scroll top of modal body if any error encountered
export function scrollToClass(className: string = '') {
  if (className) {
    const element = window?.document?.getElementsByClassName(className);
    element && element?.[0]?.scrollTo?.({ top: 0, behavior: 'smooth' });
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

//This method use to scroll into body if any error encountered
export const scrollIntoViewClass = (className: string) => {
  const element = window?.document?.getElementsByClassName(className);
  element && element?.[0]?.scrollIntoView?.({ behavior: 'smooth', inline: 'start' });
};

/**
 *
 * @param isEAA
 * @param url
 * This is used to prefix the url with subdirectory for non eaa requests. This will check for subdirectory prefix
 * if present then it will be prefixed otherwise the url is returned without prefix.
 */
export const addSubDirectoryPrefixForNonEaa = function (url = '', isEAA: boolean = false) {
  if (isEAA) {
    return url;
  } else {
    return addSubDirectoryPrefix(url);
  }
};
/** CNWEB-9188 flyout contact matrix — step-1 country + lookup phone signal drive step-2 UI. */
export const resolveCnFlyoutContactMode = (
  phoneDetails: { phoneNumber?: string; maskedNumber?: string; isChinaMobileNumber?: boolean } | undefined,
  userCountry?: string
) => {
  const hasProfilePhoneNumber = !!phoneDetails?.phoneNumber;
  const hasUnsupportedPhoneNumber = !!phoneDetails?.phoneNumber?.includes(NON_SUPPORTED_PHONE_NUMBER);
  const hasUsableExistingPhoneNumber = hasProfilePhoneNumber && !hasUnsupportedPhoneNumber;
  const hasCnDialCodeInPhone = !!phoneDetails?.phoneNumber?.includes(CHINESE_MOBILE_CODE);
  const hasExistingNonCnPhone =
    hasUsableExistingPhoneNumber && !phoneDetails?.isChinaMobileNumber && !hasCnDialCodeInPhone;
  const hasProfilePhoneSignal = hasProfilePhoneNumber || !!phoneDetails?.maskedNumber;
  const hasExistingCnPhoneSignal =
    hasProfilePhoneSignal && (phoneDetails?.isChinaMobileNumber === true || hasCnDialCodeInPhone);
  const hasExistingNonCnPhoneSignal =
    hasProfilePhoneSignal && phoneDetails?.isChinaMobileNumber === false && !hasCnDialCodeInPhone;
  const isSelectedCountryChina = userCountry?.toUpperCase() === constants.CHINA_COUNTRY_CODE;

  if (hasExistingCnPhoneSignal && isSelectedCountryChina) {
    return CN_CONTACT_MODES.EXISTING_CN_PHONE_OTP;
  }
  if (!isSelectedCountryChina || hasExistingNonCnPhone || hasExistingNonCnPhoneSignal) {
    return CN_CONTACT_MODES.EMAIL_ONLY;
  }
  return CN_CONTACT_MODES.COLLECT_CN_PHONE_OTP;
};

export const getMaskedCnPhoneDisplay = (phone = '', dialCode = CHINESE_MOBILE_CODE) => {
  const normalizedPhone = phone.replace(/\s+/g, '');
  let phoneWithoutDialCode = normalizedPhone;
  if (normalizedPhone.startsWith(dialCode)) {
    phoneWithoutDialCode = normalizedPhone.slice(dialCode.length);
  } else if (dialCode.startsWith('+') && normalizedPhone.startsWith(dialCode.slice(1))) {
    phoneWithoutDialCode = normalizedPhone.slice(dialCode.length - 1);
  }
  return phoneWithoutDialCode
    ? `${'*'.repeat(Math.max(phoneWithoutDialCode.length - 4, 0))}${phoneWithoutDialCode.slice(-4)}`
    : '';
};

//check field name should be swapped
export const shouldSwapName = (localeBasedNameSwapConfig: string, currentLocale: string): boolean => {
  const localeToCheck = currentLocale?.toLowerCase();
  const locales = localeBasedNameSwapConfig?.split(',')?.map(locale => locale.trim().toLowerCase());
  return !!localeToCheck && !!locales?.includes(localeToCheck);
};

export const isRtlDirection = () => {
  const docDirection = canUseDOM && document.dir;
  return docDirection === 'rtl';
};

export const nameRegExpValidation = (
  name: string,
  fieldType: string,
  key: string,
  setErrors: React.Dispatch<React.SetStateAction<any>>,
  errors: any,
  nameInlineErrorMsg?: string,
  checkChineseChar?: boolean
) => {
  const textFieldValidation = checkChineseChar
    ? !nameFieldRegex.test(name) && !isChinese(name)
    : !nameFieldRegex.test(name);
  if (name?.trim() && textFieldValidation) {
    if (fieldType === 'firstName') {
      setErrors({
        ...errors,
        firstNameError: '',
        [`${key}`]: nameInlineErrorMsg,
      });
    } else if (fieldType === 'lastName') {
      setErrors({
        ...errors,
        lastNameError: '',
        [`${key}`]: nameInlineErrorMsg,
      });
    } else if (fieldType === 'pinyinEnglishName') {
      setErrors({
        ...errors,
        chinaNameError: '',
        errorPinyinFirstName: '',
        [`${key}`]: nameInlineErrorMsg,
      });
    } else if (fieldType === 'pinyinEnglishSurname') {
      setErrors({
        ...errors,
        chinaSurNameError: '',
        errorPinyinLastName: '',
        [`${key}`]: nameInlineErrorMsg,
      });
    }
  } else {
    if (fieldType === 'firstName') {
      setErrors({
        ...errors,
        firstNameError: '',
        [`${key}`]: '',
      });
    } else if (fieldType === 'lastName') {
      setErrors({
        ...errors,
        lastNameError: '',
        [`${key}`]: '',
      });
    } else if (fieldType === 'pinyinEnglishName') {
      setErrors({
        ...errors,
        chinaNameError: '',
        errorPinyinFirstName: '',
        [`${key}`]: '',
      });
    } else if (fieldType === 'pinyinEnglishSurname') {
      setErrors({
        ...errors,
        chinaSurNameError: '',
        errorPinyinLastName: '',
        [`${key}`]: '',
      });
    }
  }
};
//this function will add 543 to year part of date if any string provided has date in it.
export const modifyDateStringForThai = (dateString: string): string => {
  let modifiedDateString = '';
  let i = 0;

  while (i < dateString.length) {
    // Check if the current character is a numeric digit
    if (dateString[i] >= '0' && dateString[i] <= '9') {
      // Found the start of a sequence of numeric digits
      const start = i;
      const end = i + 3;

      // Ensure we have at least 4 characters left in the string
      if (end < dateString.length && dateString[end] >= '0' && dateString[end] <= '9') {
        // Extract the 4 consecutive numeric digits
        const numericDigits = dateString.slice(start, end + 1);

        // Convert the numeric string to a number, add the given value
        const newValue = parseInt(numericDigits) + 543;

        // Append the modified numeric value to the result string
        modifiedDateString += newValue.toString();

        // Move the index past the 4 consecutive numeric digits
        i = end + 1;
      } else {
        // If not enough characters left, just append the current character
        modifiedDateString += dateString[i];
        i++;
      }
    } else {
      // Append non-numeric characters directly to the result string
      modifiedDateString += dateString[i];
      i++;
    }
  }

  return modifiedDateString;
};

//get the lang name from document lang in client side
export const getLanguageFromDocumentLang = () => {
  if (typeof document === 'undefined') return '';
  return document.documentElement?.lang?.split(/[_-]/)?.[0]?.toLowerCase();
};

//merging multiple searchparams and removing duplicates
export const mergeMultipleParams = (...params: (string | undefined)[]) => {
  const mergedParams = new URLSearchParams();

  params?.forEach((param: string | undefined) => {
    const currentParams = new URLSearchParams(param);
    for (const [key, value] of currentParams.entries()) {
      mergedParams.set(key, value);
    }
  });

  return mergedParams ? `?${mergedParams.toString()}` : '';
};

// function applied to data structures, ensuring all string values are trimmeds

export const getPayloadTrimmed = (data: any): any => {
  if (Array.isArray(data)) {
    return data.map(item => getPayloadTrimmed(item));
  }

  if (typeof data === 'object' && data !== null) {
    const trimmedData: { [key: string]: any } = {};

    Object.keys(data).forEach(key => {
      const value = data[key];
      if (typeof value === 'string') {
        trimmedData[key] = value.trim();
      } else {
        trimmedData[key] = getPayloadTrimmed(value);
      }
    });

    return trimmedData;
  }

  return data;
};

export const removeHtmlTags = (input?: string): string => {
  return input?.replace(htmlTagRegex, '') || '';
};

export const getMemberLevelPropertiesByCode = (
  code: string,
  memberStatusList: Array<{ id: string; label: string }>,
  numberOfNights?: string | number
) => {
  // Handle lifetime status mapping
  let mappedCode = code;
  if (MEMBERSHIP_LIFETIME_CODES_MAPPING[code]) {
    mappedCode = MEMBERSHIP_LIFETIME_CODES_MAPPING[code];
  } else {
    // Check if the code is already a mapped value (e.g., "PPE" for Titanium Elite)
    // In this case, we should use the code as-is
    mappedCode = code;
  }

  const member = memberStatusList?.find((item: { id: string; label: string }) => item?.id === mappedCode);
  const MEMBER_GRADIENTS = {
    ambassador: ['#1F1D20', '#3C3B3F', '#7D7C83'],
    platinum: ['#43454A', '#606267', '#A9A5A5'],
    titanium: ['#29141D', '#431E2E', '#635154'],
    default: ['#A4451D', '#C05721', '#FF965F'],
    silver: ['#575B6E', '#70758D', '#99A3CF'],
    gold: ['#6F4818', '#87673E', '#DFB47D'],
  } as const;

  switch (true) {
    case MEMBERSHIP_CODES.MEMBER_CODES.includes(code):
      return {
        backgroundColor: `${baseVariables.color['alert10']}`,
        borderLeft: `${baseVariables.color['memberSolid']}`,
        ...(numberOfNights && { bonusPoints: `${constants.SILVER_MEMBER_NIGHTS - Number(numberOfNights)}` }),
        memberLevel: member?.label,
        memerLevelBackground: `${baseVariables.color['memberSolidAlt']}`,
        gradients: MEMBER_GRADIENTS.default,
      };
    case MEMBERSHIP_CODES.SLIVER_MEMBER_CODES.includes(code):
      return {
        backgroundColor: `${baseVariables.color['alert10']}`,
        borderLeft: `${baseVariables.color['silverSolid']}`,
        bonusPoints: '10',
        memberLevel: member?.label,
        memerLevelBackground: '#F0F1F3',
        gradients: MEMBER_GRADIENTS.silver,
      };
    case MEMBERSHIP_CODES.GOLD_MEMBER_CODES.includes(code):
      return {
        backgroundColor: `${baseVariables.color['alert10']}`,
        borderLeft: `${baseVariables.color['goldSolid']}`,
        bonusPoints: '25',
        memberLevel: member?.label,
        memerLevelBackground: '#F3F0EC',
        gradients: MEMBER_GRADIENTS.gold,
      };
    case MEMBERSHIP_CODES.PLATINUM_MEMBER_CODES.includes(code):
      return {
        backgroundColor: `${baseVariables.color['alert10']}`,
        borderLeft: `${baseVariables.color['platinumSolid']}`,
        bonusPoints: '50',
        memberLevel: member?.label,
        memerLevelBackground: '#F0F1F1',
        gradients: MEMBER_GRADIENTS.platinum,
      };
    case MEMBERSHIP_CODES.TITANIUM_MEMBER_CODES.includes(code):
      return {
        backgroundColor: `${baseVariables.color['alert10']}`,
        borderLeft: `${baseVariables.color['titaniumSolid']}`,
        bonusPoints: '75',
        memberLevel: member?.label,
        memerLevelBackground: '#F2F1F1',
        gradients: MEMBER_GRADIENTS.titanium,
      };
    case MEMBERSHIP_CODES.AMBASSADOR_MEMBER_CODES.includes(code):
      return {
        backgroundColor: `${baseVariables.color['alert10']}`,
        borderLeft: `${baseVariables.color['ambassadorSolid']}`,
        bonusPoints: '75',
        memberLevel: member?.label,
        memerLevelBackground: '#F3F2F2',
        gradients: MEMBER_GRADIENTS.ambassador,
      };
    default:
      return {
        backgroundColor: `${baseVariables.color['alert10']}`,
        borderLeft: `${baseVariables.color['base10']}`,
        bonusPoints: '0',
        memberLevel: member?.label || 'Member',
        memerLevelBackground: `${baseVariables.color['memberSolidAlt']}`,
        gradients: MEMBER_GRADIENTS.default,
      };
  }
};

export const isBonvoyCardMember = (datalayer: Record<string, unknown> | undefined) => {
  if (canUseDOM) {
    const cobrandCard = datalayer?.['mr_prof_cc_all'];
    return !!cobrandCard && cobrandCard !== 'none';
  } else {
    return false;
  }
};

export const capitalizeFirstLetter = (str: string): string => {
  return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
};

// for USA, if zipcode is 9 characters and it do not have hyphen, we need to add hyphen after 5 digit - WEB-143797
export const handleUSAZipcode = (zipcode?: string) => {
  return zipcode && zipcode?.length === 9 && !zipcode?.includes('-')
    ? zipcode?.slice(0, 5) + '-' + zipcode?.slice(5)
    : zipcode || '';
};

//Replaces the single attribute of all <a> tags in a rich text HTML string with new value
export function transformAnchorAttributes(rteLabel: string, attributes: Record<string, string>): string {
  if (canUseDOM) {
    const parser = new DOMParser();
    const parsedDocument = parser?.parseFromString(rteLabel, 'text/html');
    const anchorElements = parsedDocument?.querySelectorAll('a');

    anchorElements?.forEach(anchor => {
      Object?.entries(attributes)?.forEach(([key, value]) => {
        anchor?.setAttribute(key, value);
      });
    });

    return new XMLSerializer().serializeToString(parsedDocument);
  } else {
    return rteLabel;
  }
}

export const fetchQueryParam = (queryParam: string) => {
  const urlParam = getCurrentUrlParams()?.replaceAll(/&amp;/g, '&'); // to fetch param from url
  const param = new URLSearchParams(urlParam);
  return param?.get(queryParam) ?? '';
};

// decodes base64 → text
// it return the decoded value in string
// For JSON type use JSON.parse
export const decodeBase64 = (base64String?: string) => {
  if (base64String) {
    try {
      return atob(base64String);
    } catch (error) {
      console.error('Invalid base64 string', error);
      return;
    }
  } else {
    return;
  }
};

// save referrer to history
export const saveReferrerToHistory = () => {
  if (typeof window !== 'undefined') {
    const referrer = document.referrer;
    const currentState = window.history.state || {};
    if (!window.history.state?.referrerUrl && referrer) {
      window.history.pushState({ ...currentState, referrerUrl: referrer }, '', window.location.href);
    }
  }
};

export const fetchReferrerRelativeUrl = () => {
  const referrer = document.referrer || window.history.state.referrerUrl;
  if (referrer) {
    const refUrl = new URL(referrer, window.location.origin);
    return refUrl.pathname + refUrl.search + refUrl.hash;
  }
  return '';
};

export const ternaryHandler = (value: unknown, defaultValue: string | boolean | unknown = '') => {
  return value ?? defaultValue;
};

/**
 * Recursively sanitizes an object by:
 * - Removing keys with 'null','undefined' or empty string values.
 * - Recursively processing nested objects
 * - Preserving only non-empty objects after sanitization
 *
 * @template T
 * @param {T} obj - The object to sanitize
 *
 * @returns {dataLayerType} - A new sanitized object with unwanted keys removed
 *
 * @example
 * const input = {name:"john",age:null}
 * const output=sanitizeObject(input)
 * console.log(output) // {name:"john"}
 */
export function sanitizeObject(obj: dataLayerType) {
  if (typeof obj !== 'object' || obj === null) return obj;

  const cleanedObj: { [key: string]: unknown } = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value === '' || value === null || value === undefined) {
      continue; // Skip keys with "", null, or undefined
    }
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      const nested = sanitizeObject(value as dataLayerType);
      // Only add if nested object is not empty
      if (Object.keys(nested ?? {}).length > 0) {
        cleanedObj[key] = nested;
      }
    } else {
      cleanedObj[key] = value;
    }
  }

  return cleanedObj;
}

/**
 * Calculates number of days sincs join date
 *
 * @template T
 * @param {T} obj - The object to sanitize
 *
 * @returns {dataLayerType} - A new sanitized object with unwanted keys removed
 *
 * @example
 * const input = {mr_prof_join_date:"08/23/2025"}
 * const output=accountAgeCalculator(input)
 * console.log(output) // {"100"}
 */
export function accountAgeCalculator(datalayer: dataLayerType) {
  if (datalayer?.['memState'] === 'authenticated') {
    const joinDate = (ternaryHandler(datalayer?.['mr_prof_join_date']) as string).replaceAll('-', '/');
    const joinDateObj = new Date(joinDate);
    const today = new Date();

    //calculate time difference in milliseconds
    const diffInMs: number = today.getTime() - joinDateObj.getTime();

    //convert ms -> days (1 day = 86400000ms)
    const diffInDays = Math.round(diffInMs / 86400000);

    //ensure it's valid number
    if (!isNaN(diffInDays)) {
      return diffInDays.toString();
    }
    return '';
  }
  return '';
}

export const updateDateFormat = (input: string, format = 'MM/DD/YYYY') => {
  if (input === '') {
    return '';
  }
  const parsedDate = dayjs(input);
  if (!parsedDate.isValid()) {
    return '';
  }
  return parsedDate.format(format);
};

export const scrollToTargetClass = (className: string) => {
  const element = window?.document?.getElementsByClassName(className);
  element && element?.[0]?.scrollIntoView?.({ behavior: 'smooth', inline: 'start', block: 'center' });
};

export const capitalizeWords = (input: string) => {
  return input
    .toLowerCase() // Convert entire string to lowercase
    .split(/\s+/) // Split by spaces (handles multiple spaces)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Utility function to check if a value is NOT empty, null, undefined, or "''"
export const isValidValue = (val?: string) => {
  return !(val === undefined || val === null || val === '' || val === "''");
};

/**
 * Check if a URL is external (starts with http://, https://, or //)
 * @param url - The URL to check
 * @returns true if the URL is external, false otherwise
 */
export const isExternalUrl = (url: string): boolean => {
  if (!url) return false;
  return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//');
};

/**
 * Tracking configuration for RichText link injection
 */
export interface RichTextLinkTracking {
  /** Text content or partial match to identify the link */
  linkText?: string;
  /** URL or partial URL match to identify the link */
  linkHref?: string;
  /** The tracking value to inject (pipe-delimited format) */
  trackingValue: string;
}

/**
 * Injects click tracking attributes into anchor tags within RichText HTML content.
 * Matches links by text content or href and adds data-custom_click_track_value attribute.
 *
 * @param htmlString - The HTML string containing anchor tags
 * @param trackingConfigs - Array of tracking configurations to match and inject
 * @returns Modified HTML string with tracking attributes added
 *
 * @example
 * const html = '<a href="https://help.marriott.com">FAQ</a>';
 * const tracking = [{ linkText: 'FAQ', trackingValue: 'Modal|FAQ|external' }];
 * const result = injectTrackingIntoRichTextLinks(html, tracking);
 * // Returns: '<a href="https://help.marriott.com" data-custom_click_track_value="Modal|FAQ|external" class="custom_click_track">FAQ</a>'
 */
export const injectTrackingIntoRichTextLinks = (
  htmlString: string,
  trackingConfigs: RichTextLinkTracking[]
): string => {
  if (!canUseDOM || !htmlString || !trackingConfigs || trackingConfigs.length === 0) {
    return htmlString;
  }

  const parser = new DOMParser();
  const parsedDocument = parser.parseFromString(htmlString, 'text/html');
  const anchorTags = parsedDocument.querySelectorAll('a');

  anchorTags.forEach(link => {
    const linkText = link.textContent?.trim().toLowerCase() || '';
    const linkHref = link.getAttribute('href')?.toLowerCase() || '';

    // Find matching tracking config
    const matchingConfig = trackingConfigs.find(config => {
      if (config.linkText && linkText.includes(config.linkText.toLowerCase())) {
        return true;
      }
      if (config.linkHref && linkHref.includes(config.linkHref.toLowerCase())) {
        return true;
      }
      return false;
    });

    if (matchingConfig) {
      link.setAttribute('data-custom_click_track_value', matchingConfig.trackingValue);
      link.classList.add('custom_click_track');
    }
  });

  // Serialize back to string
  const modifiedString = new XMLSerializer().serializeToString(parsedDocument);
  return modifiedString;
};

// it prepends the correct domain to the url
export const prependDomainIfMissing = (url: string, base?: string): string | undefined => {
  const s = url?.trim();
  if (!s || isExternalUrl(s)) return url;
  return `${base}${s.startsWith('/') ? s : '/' + s}`;
};

// TEMPORARY fallback util for label migration cleanup
// TODO: Remove this util and all usages after fallback migration is complete
export function handleFallback<T>(primary: T, fallback: T): T {
  return primary || fallback;
}

export const normalizeWebPromptLocaleTag = (value?: string) => (value ?? '').trim().replace(/_/g, '-').toLowerCase();

// Returns true if the flag locale is 'all' (wildcard enabling all locales),
// or if it matches the current active locale tag exactly.
export const isWebPromptLocaleEnabled = (webPromptEnabledLocalesFlag: unknown, currentLocale?: string) => {
  const activeLocaleTag = normalizeWebPromptLocaleTag(currentLocale);

  return (
    typeof webPromptEnabledLocalesFlag === 'string' &&
    webPromptEnabledLocalesFlag.split(',').some(flagLocaleToken => {
      const normalizedFlagLocale = normalizeWebPromptLocaleTag(flagLocaleToken);
      return (
        normalizedFlagLocale === 'all' || (normalizedFlagLocale?.length > 0 && normalizedFlagLocale === activeLocaleTag)
      );
    })
  );
};

// PAGE_LOAD_TRACKING_KEY is used to track if the page has been loaded in the session storage.
// This can be useful for scenarios where you want to remember if a user has visited a specific page or completed a certain action during their session.
export const PAGE_LOAD_TRACKING_KEY = 'rememberedOnHybridR0Checkout';

//trackAsLoaded function sets a specific key in the session storage to indicate that a page has been loaded.
// This can be useful for tracking user interactions or ensuring that certain actions are only performed once per session.
// The default key used is PAGE_LOAD_TRACKING_KEY, but it can be overridden by providing a different key as an argument.
export const trackAsLoaded = (key = PAGE_LOAD_TRACKING_KEY) => {
  sessionStorage.setItem(key, 'true');
};

type CalendarStartDate = ComponentProps<typeof Calendar>['startDate'];

export const getCalendarStartDate = (date?: Date): CalendarStartDate => {
  if (!date) {
    return null;
  }

  const parsedDate = dayjs(date);
  return parsedDate.isValid() ? (parsedDate as unknown as CalendarStartDate) : null;
};

// Constructs a URL for either the ADF or RLM navigation based on the search type and provided parameters
export const constructAdfOrRlmURLWithDate = (
  availabilitySearchURL: string,
  navigationADFURL: string,
  navigationRLMURL: string,
  isFlexibleDateSearch: boolean,
  propertyID: string,
  currentCurrency: string,
  includeTaxesAndFees: boolean,
  startDate?: string,
  endDate?: string
): string => {
  const hasDates = Boolean(startDate && endDate);

  const baseURL = isFlexibleDateSearch
    ? addSubDirectoryPrefix(`${availabilitySearchURL}${navigationADFURL}`)
    : addSubDirectoryPrefix(`${availabilitySearchURL}${navigationRLMURL}`);

  const searchTypeParams = isFlexibleDateSearch
    ? '&isSearch=true&currency='
    : [
        hasDates ? `&fromDate=${encodeURIComponent(startDate!)}` : '',
        hasDates ? `&toDate=${encodeURIComponent(endDate!)}` : '',
        `&flexibleDateSearch=${!hasDates}`,
        '&isSearch=true&currency=',
        currentCurrency === 'default' ? '' : encodeURIComponent(currentCurrency),
        `&showFullPrice=${includeTaxesAndFees}`,
        currentCurrency === 'default' ? '' : '&redirectedFromSERP=true',
      ].join('');

  return baseURL + propertyID + searchTypeParams;
};

// Converts a date string to a specified output format using dayjs
export const convertDateToFormate = (date: string | undefined, outputFormat: string): string | undefined => {
  if (!date) return undefined;

  const parsedDate = dayjs(date);

  return parsedDate.isValid() ? parsedDate.format(outputFormat) : undefined;
};
